import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsultationService from '../../services/ConsultationService';

interface FormState {
  name: string;
  phone: string;
  email: string;
  userQuestion: string;
  privacy: boolean;
}

interface RegisterFormProps {
  initText?: string;
}

/**
 * Register Form Component
 * Allows users to submit consultation requests
 * Features: Framer Motion animations, teal color scheme, enhanced UX
 */
const RegisterForm: React.FC<RegisterFormProps> = ({ initText = '' }) => {
  const [formState, setFormState] = useState<FormState>({
    name: '',
    phone: '',
    email: '',
    userQuestion: initText || '',
    privacy: false,
  });

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for dark mode preference
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }

    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleDarkModeChange = (event: MediaQueryListEvent) => {
      setIsDarkMode(event.matches);
    };

    darkModeMediaQuery.addEventListener('change', handleDarkModeChange);
    return () => darkModeMediaQuery.removeEventListener('change', handleDarkModeChange);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formState.name || !formState.phone || !formState.email) {
        setError('Please fill in all required fields');
        setIsLoading(false);
        return;
      }

      if (!formState.privacy) {
        setError('Please agree to the privacy policy');
        setIsLoading(false);
        return;
      }

      // Submit form
      const response = await ConsultationService.submitConsultationRequest({
        fullName: formState.name,
        email: formState.email,
        phone: formState.phone,
        message: formState.userQuestion,
      });

      if (response.statusCode >= 200 && response.statusCode < 300) {
        setIsSubmitted(true);
        setFormState({
          name: '',
          phone: '',
          email: '',
          userQuestion: '',
          privacy: false,
        });

        // Reset after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 3000);
      } else {
        setError(response.error || 'Failed to submit form. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      <motion.div
        className={`relative max-w-md mx-auto rounded-xl overflow-hidden shadow-2xl backdrop-blur-md ${
          isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'
        }`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        whileHover={{
          boxShadow: isDarkMode
            ? '0 25px 50px -12px rgba(1, 126, 110, 0.4)'
            : '0 25px 50px -12px rgba(1, 126, 110, 0.3)',
        }}
      >
        {/* Decorative elements - teal theme */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-gradient-to-br from-[#017e6e]/40 to-emerald-400/20 blur-xl"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity }}
          ></motion.div>
          <motion.div
            className="absolute -bottom-16 -left-16 w-32 h-32 rounded-full bg-gradient-to-tr from-[#017e6e]/30 to-cyan-400/10 blur-xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 10, repeat: Infinity }}
          ></motion.div>
        </div>

        {/* Success message overlay */}
        <AnimatePresence>
          {isSubmitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-xl"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 0.6, type: 'spring' }}
              >
                <svg
                  className="w-16 h-16 text-green-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <h3 className="mt-4 text-xl font-bold text-green-600 dark:text-green-400">Success!</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 text-center px-4">
                Thank you for registering. We'll contact you soon.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form content */}
        <div className="relative p-6 sm:p-8 z-10">
          {/* Form header */}
          <motion.div
            className="mb-8 text-center"
            initial={{ y: -10, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-black dark:text-black mb-2">
              Nhận tư vấn ngay
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Điền thông tin để nhận tư vấn khóa học phù hợp
            </p>
          </motion.div>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-3 bg-red-500/20 border border-red-500/50 dark:border-red-500 rounded-lg text-red-600 dark:text-red-400 text-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form fields */}
          <motion.form
            onSubmit={handleSubmit}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-4"
          >
            {/* Name Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Họ và tên (*)
              </label>
              <motion.input
                type="text"
                name="name"
                value={formState.name}
                onChange={handleChange}
                placeholder="Họ và tên"
                required
                className={`w-full px-4 py-3 rounded-lg border transition-all ${
                  isDarkMode
                    ? 'bg-gray-800/50 border-gray-700 text-white focus:border-[#017e6e]'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#017e6e]'
                } focus:outline-none focus:ring-2 focus:ring-[#017e6e]/20`}
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Email Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Email (*)
              </label>
              <motion.input
                type="email"
                name="email"
                value={formState.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className={`w-full px-4 py-3 rounded-lg border transition-all ${
                  isDarkMode
                    ? 'bg-gray-800/50 border-gray-700 text-white focus:border-[#017e6e]'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#017e6e]'
                } focus:outline-none focus:ring-2 focus:ring-[#017e6e]/20`}
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Phone Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Số điện thoại (*)
              </label>
              <motion.input
                type="tel"
                name="phone"
                value={formState.phone}
                onChange={handleChange}
                placeholder="+84 (0) 12345678"
                required
                className={`w-full px-4 py-3 rounded-lg border transition-all ${
                  isDarkMode
                    ? 'bg-gray-800/50 border-gray-700 text-white focus:border-[#017e6e]'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#017e6e]'
                } focus:outline-none focus:ring-2 focus:ring-[#017e6e]/20`}
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Message Field */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Thông tin bạn quan tâm
              </label>
              <motion.textarea
                name="userQuestion"
                value={formState.userQuestion}
                onChange={handleChange}
                placeholder="Thông tin bạn quan tâm là gì?"
                rows={3}
                className={`w-full px-4 py-3 rounded-lg border transition-all resize-none ${
                  isDarkMode
                    ? 'bg-gray-800/50 border-gray-700 text-white focus:border-[#017e6e]'
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#017e6e]'
                } focus:outline-none focus:ring-2 focus:ring-[#017e6e]/20`}
                whileFocus={{ scale: 1.02 }}
              />
            </motion.div>

            {/* Privacy Checkbox */}
            <motion.div variants={itemVariants} className="flex items-start space-x-3 pt-2">
              <motion.input
                type="checkbox"
                name="privacy"
                checked={formState.privacy}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#017e6e] cursor-pointer accent-[#017e6e]"
                whileFocus={{ scale: 1.2 }}
                required
              />
              <label className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                Bằng việc đăng ký thông tin, bạn đồng ý cho phép trung tâm Anh Ngữ liên lạc thông qua các hình thức:
                cuộc gọi, tin nhắn, email nhằm mục đích tư vấn các chương trình và khóa học. (*)
              </label>
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={itemVariants} className="flex justify-center pt-4">
              <motion.button
                type="submit"
                disabled={isLoading || !formState.privacy}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4)',
                }}
                whileTap={{ scale: 0.98 }}
                className="relative overflow-hidden text-white font-semibold py-3 px-8 rounded-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 shadow-md disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    ĐANG XỬ LÝ...
                  </>
                ) : (
                  <>
                    <span>ĐĂNG KÝ</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                    </svg>
                  </>
                )}

                {/* Moving light effect */}
                <motion.div
                  className="absolute top-0 left-0 w-20 h-full bg-white/20 skew-x-[20deg]"
                  animate={{
                    left: ['-100%', '200%'],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2.5,
                    ease: 'easeInOut',
                    repeatDelay: 1,
                  }}
                />
              </motion.button>
            </motion.div>
          </motion.form>

          {/* Footer text */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="text-xs text-center mt-4 opacity-60"
          >
            Chúng tôi bảo vệ quyền riêng tư của bạn. Thông tin của bạn an toàn với chúng tôi.
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterForm;
